/**
 * The presenter overlay, mounted once by the Shell and drawn only in
 * presenter mode: a slim caption bar at the bottom (beat number, title,
 * caption, progress dots, tour countdown), a corner badge row (clock state,
 * demo data, offline readiness), a "not ready" note when a beat timed out,
 * the transient help sheet, and the docks a beat asks for while the shell
 * hides its chrome (the bottom panel for the Gantt and the coverage gaps,
 * the GAIA column). Keyboard driven: everything ignores the pointer except
 * the help sheet and the GAIA column, which takes typing.
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import {
  CloudOff,
  Database,
  FastForward,
  HardDriveDownload,
  Keyboard,
  Radio,
  Snowflake,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useClock, useUI, type Mode } from '@/state/store';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { BottomPanel } from '@/features/shell/BottomPanel';
import { useOffline } from '@/features/offline';
import { useTwin } from '@/features/twin/store';
import { BEATS, BEAT_KEYS } from './beats';
import { modeFromPath } from './controller';
import { PRESENTER_KEYS } from './keyboard';
import { offlineBadge } from './offline';
import { usePresenter, type PresenterNote } from './store';

const GLOBE_MODES = new Set<Mode>(['loader', 'operations', 'analysis']);
const HELP_AUTO_CLOSE_MS = 12_000;

// Lazy like the Shell's own GAIA column, so the GAIA code stays out of the shell chunk.
const GaiaColumn = lazy(() => import('@/features/gaia/GaiaColumn').then((m) => ({ default: m.GaiaColumn })));

export function PresenterOverlay() {
  const presenter = useUI((s) => s.presenter);
  if (!presenter) return null;
  return <PresenterLayer />;
}

function PresenterLayer() {
  const { pathname } = useLocation();
  const mode = modeFromPath(pathname);
  const activeIndex = usePresenter((s) => s.activeIndex);
  const captionVisible = usePresenter((s) => s.captionVisible);
  const helpOpen = usePresenter((s) => s.helpOpen);
  const lastError = usePresenter((s) => s.lastError);
  const gaiaOpen = useUI((s) => s.gaiaOpen);
  const bottomTab = useUI((s) => s.bottomTab);
  const procedureRunning = useTwin((s) => s.procedure !== null);
  const beat = activeIndex === null ? null : BEATS[activeIndex];

  const showDock = Boolean(beat?.show?.bottomPanel) && mode !== null && GLOBE_MODES.has(mode);
  const showGaia = Boolean(beat?.show?.gaia) && gaiaOpen;
  // The twin HUD's procedure strip sits at the bottom centre of the stage; the caption rides above it.
  const liftForProcedure = !showDock && mode === 'maintenance' && procedureRunning;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-40 flex flex-col justify-end"
      data-testid="presenter-overlay"
    >
      <style>{CAPTION_KEYFRAMES}</style>
      {showGaia && (
        <aside
          className="pointer-events-auto absolute inset-y-0 left-0 w-[360px] overflow-hidden border-r border-hairline bg-raised"
          aria-label="GAIA assistant"
          data-gaia-root=""
          data-testid="presenter-gaia"
        >
          <Suspense fallback={<div className="p-4 text-xs text-ink-3">Loading GAIA</div>}>
            <GaiaColumn />
          </Suspense>
        </aside>
      )}

      <div
        className={cn(
          'grid grid-cols-[minmax(176px,1fr)_minmax(0,760px)_minmax(176px,1fr)] items-end gap-4 px-4',
          liftForProcedure ? 'pb-[92px]' : 'pb-4',
          showGaia && 'pl-[376px]',
        )}
      >
        <div />
        <div className="flex min-w-0 flex-col gap-2">
          {lastError && lastError.beatIndex === activeIndex && <BeatNote note={lastError} />}
          <CaptionBar index={activeIndex} visible={captionVisible} />
        </div>
        <BadgeRow />
      </div>

      {showDock && mode && (
        <div data-testid="presenter-dock" data-tab={bottomTab}>
          <BottomPanel mode={mode} />
        </div>
      )}

      {helpOpen && <HelpSheet />}
    </div>
  );
}

/** Caption text swaps with a short fade; the global reduced-motion rule shortens it to nothing. */
const CAPTION_KEYFRAMES =
  '@keyframes presenter-caption-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }';

function CaptionBar({ index, visible }: { index: number | null; visible: boolean }) {
  const applying = usePresenter((s) => s.applying);
  const beat = index === null ? null : BEATS[index];
  return (
    <section
      data-testid="presenter-caption"
      data-state={applying ? 'applying' : 'shown'}
      aria-label="Story caption"
      aria-live="polite"
      aria-hidden={!visible}
      className={cn(
        'overflow-hidden rounded-panel border border-hairline-strong bg-raised/90 shadow-elev-2 backdrop-blur transition-[opacity,transform,visibility] duration-200 ease-out',
        visible ? 'visible opacity-100' : 'invisible translate-y-2 opacity-0',
      )}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          data-testid="presenter-beat-number"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-accent-soft font-mono text-lg font-semibold text-accent"
        >
          {beat ? index! + 1 : 'P'}
        </div>
        <div
          key={beat?.id ?? 'idle'}
          className="min-w-0 flex-1"
          style={{ animation: 'presenter-caption-in 200ms ease-out' }}
        >
          <div className="truncate font-display text-sm font-semibold text-ink">
            {beat?.title ?? 'Presenter mode'}
          </div>
          <div className="line-clamp-2 text-[13px] leading-snug text-ink-2">
            {beat?.caption ??
              'Press 1 to 0 for a story beat, Space to begin, T for the auto-tour, ? for every key.'}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <ProgressDots index={index} applying={applying} />
          <TourStatus />
        </div>
      </div>
      <TourBar />
    </section>
  );
}

function ProgressDots({ index, applying }: { index: number | null; applying: boolean }) {
  return (
    <div
      className="flex items-center gap-1"
      aria-label={index === null ? 'No beat yet' : `Beat ${index + 1} of ${BEATS.length}`}
    >
      {BEATS.map((b, i) => (
        <span
          key={b.id}
          title={`${BEAT_KEYS[i]}: ${b.title}`}
          className={cn(
            'h-1.5 rounded-full transition-[width,background-color] duration-200',
            i === index ? 'w-4 bg-accent' : 'w-1.5',
            i === index && applying && 'animate-pulse',
            i !== index && (index !== null && i < index ? 'bg-ink-3' : 'bg-hairline-strong'),
          )}
        />
      ))}
    </div>
  );
}

/** Wall-clock now, re-read on an interval while enabled (for the tour countdown). */
function useWallClock(enabled: boolean, intervalMs = 250): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [enabled, intervalMs]);
  return now;
}

function TourStatus() {
  const touring = usePresenter((s) => s.touring);
  const frozen = usePresenter((s) => s.frozen);
  const deadline = usePresenter((s) => s.tourDeadline);
  const now = useWallClock(touring && deadline !== null);
  if (!touring) {
    return (
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-ink-3">
        <Keyboard className="h-3 w-3" /> ? keys
      </span>
    );
  }
  const label = frozen
    ? 'Tour paused'
    : deadline === null
      ? 'Tour'
      : `Tour ${Math.max(0, Math.ceil((deadline - now) / 1000))} s`;
  return (
    <span data-testid="presenter-tour" className="font-mono text-[10px] uppercase tracking-wider text-accent">
      {label}
    </span>
  );
}

function TourBar() {
  const touring = usePresenter((s) => s.touring);
  const deadline = usePresenter((s) => s.tourDeadline);
  const total = usePresenter((s) => s.tourTotalMs);
  const remaining = usePresenter((s) => s.tourRemainingMs);
  const now = useWallClock(touring && deadline !== null);
  if (!touring || !total) return null;
  const left = deadline !== null ? Math.max(0, deadline - now) : (remaining ?? total);
  return (
    <div className="h-0.5 w-full bg-inset">
      <div
        className="h-full bg-accent transition-[width] duration-200"
        style={{ width: `${(left / total) * 100}%` }}
      />
    </div>
  );
}

function BeatNote({ note }: { note: PresenterNote }) {
  const parts =
    note.kind === 'error' ? [note.message] : [...note.missing, ...note.skipped.map((s) => `skipped ${s}`)];
  return (
    <div
      role="status"
      data-testid="presenter-note"
      className={cn(
        'flex items-start gap-2 self-start rounded-panel border bg-raised/90 px-3 py-1.5 text-xs text-ink shadow-elev-1 backdrop-blur',
        note.kind === 'error' ? 'border-warning/50' : 'border-caution/50',
      )}
    >
      <TriangleAlert
        className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', note.kind === 'error' ? 'text-warning' : 'text-caution')}
      />
      <span>
        Beat {note.beatIndex + 1} {note.kind === 'error' ? 'failed' : 'is not fully ready'}:{' '}
        {parts.join('; ')}
        {note.skipped.length > 0 && `. Press ${BEATS[note.beatIndex]?.key} to try it again.`}
      </span>
    </div>
  );
}

/** Chips are translucent; the backing keeps them legible over the globe and the twin. */
function Badge({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-raised/90 shadow-elev-1 backdrop-blur">{children}</span>;
}

function BadgeRow() {
  const frozen = usePresenter((s) => s.frozen);
  const demoProfile = usePresenter((s) => s.demoProfile);
  const live = useClock((s) => s.live);
  const multiplier = useClock((s) => s.multiplier);
  const offline = useOffline(offlineBadge);
  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="presenter-badges">
      <Badge>
        {frozen ? (
          <Chip tone="caution">
            <Snowflake className="h-3 w-3" /> FROZEN
          </Chip>
        ) : live ? (
          <Chip tone="live" pulse>
            <Radio className="h-3 w-3" /> LIVE
          </Chip>
        ) : (
          <Chip tone="sim">
            <FastForward className="h-3 w-3" /> SIM {multiplier}x
          </Chip>
        )}
      </Badge>
      {demoProfile && (
        <Badge>
          <Chip tone="neutral">
            <Database className="h-3 w-3" /> DEMO DATA
          </Chip>
        </Badge>
      )}
      <Badge>
        {offline === 'ready' ? (
          <Chip tone="nominal">
            <HardDriveDownload className="h-3 w-3" /> OFFLINE READY
          </Chip>
        ) : (
          <Chip tone="offline">
            <CloudOff className="h-3 w-3" /> OFFLINE NOT CACHED
          </Chip>
        )}
      </Badge>
    </div>
  );
}

function HelpSheet() {
  const setHelpOpen = usePresenter((s) => s.setHelpOpen);
  useEffect(() => {
    const timer = window.setTimeout(() => setHelpOpen(false), HELP_AUTO_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [setHelpOpen]);
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Presenter keys"
      data-presenter-help=""
      data-testid="presenter-help"
      className="pointer-events-auto absolute left-1/2 top-1/2 w-[min(460px,calc(100%-32px))] -translate-x-1/2 -translate-y-1/2 rounded-panel border border-hairline-strong bg-raised/95 p-4 shadow-elev-2 backdrop-blur"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-2">Presenter keys</div>
        <button
          type="button"
          aria-label="Close"
          onClick={() => setHelpOpen(false)}
          className="text-ink-3 hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {PRESENTER_KEYS.map((row) => (
            <tr key={row.keys} className="border-t border-hairline">
              <td className="whitespace-nowrap py-1.5 pr-4 font-mono text-accent">{row.keys}</td>
              <td className="py-1.5 text-ink-2">{row.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ol className="mt-3 grid grid-cols-2 gap-x-4 gap-y-0.5 border-t border-hairline pt-2 text-[11px] text-ink-3">
        {BEATS.map((b, i) => (
          <li key={b.id} className="truncate">
            <span className="font-mono text-ink-2">{BEAT_KEYS[i]}</span> {b.title}
          </li>
        ))}
      </ol>
    </div>
  );
}
